import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ConfirmationService, MessageService } from "primeng/api";
import { BehaviorSubject, Observable, Subject, catchError, delayWhen, forkJoin, map, of, retry, retryWhen, scan, switchMap, timer } from "rxjs";
import { CourseConstants } from "../constants/course.constants";
import { CourseService } from "./course.service";
import { Course } from "../models/course";
import { TrainingMaterial } from "../models/training-material";
import { TrainingMaterialSave } from "../models/training-material-save";
import { Attachment } from "../../my-space/user-profile/models/attachment";
import { sessionAttendeeDTO } from "../models/sessionAttendeeDTO";
import { Prerequisite } from "../models/prerequisites";
import { PrerequisitesData } from "../models/prerequisitesData";
import { EnrollSessionData } from "../models/enroll-session-data";
import { SessionDetails } from "../models/session-details";
import { SessionAttendeePassOrFailConstants } from "../constants/sessionAttendeePassOrFail.constants";
import { SharedConstants } from "../../../app/shared/constants/shared-constants";
import { CommonService } from "../../../app/shared/services/common.service";

@Injectable({
  providedIn: 'root',
})
export class CoursePersistenceService {

  closeAndResetCourseSlider$ = new Subject<boolean>();
  enrolledSession$ = new BehaviorSubject<boolean>(false);
  withdrawSession$ = new BehaviorSubject<boolean>(false);
  closeAndResetSessionAttendeeSlider$ = new Subject<boolean>();
  courseDetails$ = new BehaviorSubject<Course | null>(null);
  sessionUpdateData$ = new BehaviorSubject<SessionDetails | null>(null);
  trainingMaterial$ = new BehaviorSubject<TrainingMaterial | null>(null);
  prerequisites$ = new BehaviorSubject<Prerequisite[] | null>(null);
  courseSaveInProgress$ = new Subject<boolean>();
  errorTabIndices: number[] = [];
  populateErrorsForTab$ = new BehaviorSubject<boolean>(false);
  currentTabInSliderMenuByCustomChange$ = new Subject<number>();
  currentTabInSliderMenu$ = new Subject<number>();
  trainingMaterialAttachments: File[] = [];
  filesToSave: Attachment[] = [];
  isEdit: boolean = false;
  filesToDelete: string[] = [];
  deletedCourseAttachments: Attachment[] = [];
  validatedUsersList: any = [];
  usersList: any = [];
  completeDateModalVisible$ = new Subject<boolean>();
  sessionIdValue: any;
  sessionAttendeesValue: sessionAttendeeDTO[] = [];
  passedAttendeesList: sessionAttendeeDTO[] = [];
  passingScore!: number;
  savedTrainingMaterialAttachments: File[] = []
  courseId!: string;
  
  constructor(private courseService: CourseService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router,
    private commonService: CommonService
  ) { }



  openConfirmationDialog = () => {
    this.confirmationService.confirm({
      message: CourseConstants.Course_Slider_Confirmation_Text,
      header: CourseConstants.Course_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetCourseSlider$.next(true);
      },
    });
  }

  openConfirmationDialogForCancelSession = (sessionId: any) => {
    this.confirmationService.confirm({
      message: CourseConstants.Cancel_Session_Confirmation_Text,
      header: CourseConstants.Cancel_Session_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.courseService.cancelSession(sessionId).subscribe({
          next: (resp) => {
            this.messageService.add({
              severity: CourseConstants.Severity_Success,
              summary: `${CourseConstants.Session_Cancelled_Success}`,
            });
          }
        });
      },
    });
  }

  openConfirmationDialogForSessionAttendee = () => {
    this.confirmationService.confirm({
      message: CourseConstants.Session_Attendee_Confirmation_Text,
      header: CourseConstants.Session_Attendee_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetSessionAttendeeSlider$.next(true);
      }
    });
  }

  openConfirmationDialogForEnrollment = (sessionId: any, enrollSessionData: EnrollSessionData) => {
    this.confirmationService.confirm({
      message: CourseConstants.Enroll_Session_Confirm_Text,
      header: CourseConstants.Session_Enrollment_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.courseService.enrollSession(sessionId, enrollSessionData).subscribe({
          next: (resp) => {
            if (resp) {
              this.messageService.add({
                severity: CourseConstants.Severity_Success,
                summary: `${CourseConstants.Enrollment_Session_Confirmation_Header}`,
              });
              this.enrolledSession$.next(false);
            }
          },
          error: (error) => {
            if (error.status === 412) {
              this.messageService.add({
                severity: CourseConstants.Severity_Error,
                summary: `${CourseConstants.Enrollment_Session_validation_Message}`,
                sticky: true
              });
            } else {
              this.messageService.add({
                severity: CourseConstants.Severity_Error,
                summary: CourseConstants.CourseApi_Error_Message,
              });
            }
          },
        });
        this.enrolledSession$.next(false);
      },
    });
  }

  openConfirmationDialogForWithdrawn = (sessionId: any, recommendedMaxAttendee: any) => {
    this.confirmationService.confirm({
      message: CourseConstants.Withdrawal_Session_Confirm_Text,
      header: CourseConstants.Session_Wwithdraw_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.courseService.withdrawSession(sessionId, recommendedMaxAttendee).subscribe({
          next: (resp) => {
            this.messageService.add({
              severity: CourseConstants.Severity_Success,
              summary: `${CourseConstants.Withdrawal_Session_Success_Header}`,
            });
          }
        });
        this.withdrawSession$.next(false);
      }
    });
  }

  openConfirmationDialogForSessionAttendeeUpdate = (sessionId: any, sessionAttendees: sessionAttendeeDTO[]) => {
    this.confirmationService.confirm({
      message: CourseConstants.Session_AttendeeUpdate_Confirmation_Text,
      header: CourseConstants.Session_AttendeeUpdate_Confirmation_header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.sessionIdValue = sessionId;
        this.sessionAttendeesValue = sessionAttendees;
        this.openCompleteDateModal();
      }
    });
  }

  openCompleteDateModal() {
    if (this.passedAttendeesList.length > 0 && this.passedAttendeesList.some(data => data.passOrFail === SessionAttendeePassOrFailConstants.AttendeeStatus_Pass)) {
      this.completeDateModalVisible$.next(true);
    }
    else if (this.passedAttendeesList.some(data => data.grade != null && data.grade !== undefined && data.grade >= this.passingScore)) {
      this.completeDateModalVisible$.next(true);
    }
    else {
      this.upateSessionAttendee(this.sessionIdValue, this.sessionAttendeesValue);
    }

  }

  updateSessionAttendeeFromTainingCompleteDateModal(completeDate: any): void {
    this.sessionAttendeesValue.forEach(data => {
      // Find if the attendee is in the passedAttendeesList
      const isAttendeeSelected = this.passedAttendeesList.some(selectedAttendeeData => selectedAttendeeData.attendee === data.attendee);

      if (isAttendeeSelected) {
        data.completeDate = this.commonService.conversionOfDateIntoLocalDateTime(completeDate);
      }
    });

    // Update the session attendees after changing the completeDate
    this.upateSessionAttendee(this.sessionIdValue, this.sessionAttendeesValue);
  }


  sendSelectedPassedAttendees(selectedPassedAttendeesList: any) {
    this.passedAttendeesList = selectedPassedAttendeesList;
  }

  upateSessionAttendee(sessionId: any, sessionAttendees: sessionAttendeeDTO[]) {
    this.courseService.updateSessionAttendee(sessionId, sessionAttendees).subscribe({
      next: (resp: SessionDetails) => {
        this.sessionUpdateData$.next(resp);
        this.messageService.add({
          severity: CourseConstants.Severity_Success,
          summary: `${CourseConstants.Session_AttendeeUpdate_Sucess_Heade}`,
        })
      }
    });
    this.passedAttendeesList = [];
    this.closeAndResetSessionAttendeeSlider$.next(true);
  }

  updateFileDetails = (courseAttachments: File[]) => {
    this.trainingMaterialAttachments = courseAttachments;
  }

  updateCourseDetails = (courseDetails: Course) => {
    this.courseDetails$.next(courseDetails);
  }

  updateTrainingMaterialDetails = (trainingMaterial: TrainingMaterial | null) => {
    this.trainingMaterial$.next(trainingMaterial);
  }

  updatePrerequisties = (prerequisites: Prerequisite[] | null) => {
    this.prerequisites$.next(prerequisites);
  }

  updateSaveInProgressStatus = (status: boolean) => {
    this.courseSaveInProgress$.next(status);
  }
  updateTrainingMaterialAttachmentFiles = (files: File[]) => {
    this.savedTrainingMaterialAttachments = files;
  }

  saveCourse(isEditCourse: boolean) {
    let courseToSave = this.courseDetails$.getValue();
    // if courseToSave detail from General Tab is not null, then Proceed for Save
    if (!!courseToSave) {
      if (this.prerequisites$.getValue() !== null) {
        let prerequisites = this.prerequisites$.getValue()
        courseToSave!.prerequisites = prerequisites;
      }
      if (this.trainingMaterial$.getValue() !== null) {
        const trainingMaterialInfo = this.constructTrainingMaterial();
        courseToSave!.trainingMaterial = trainingMaterialInfo;
      }

      this.courseService.saveCourse(courseToSave).pipe(
        switchMap((resp) => {
          this.courseId = resp.courseId;
          return this.courseService.getCourseFromId(this.courseId).pipe(
            retryWhen(errors =>
              errors.pipe(
                scan((retryCount, error) => {
                  if (retryCount >= 4) {
                    this.messageService.add({
                      severity: CourseConstants.Severity_Error,
                      summary: `${CourseConstants.Course_Created_Failed}`
                    });
                    throw error;
                  }
                  return retryCount + 1;
                }, 0),
                delayWhen(() => timer(3000))
              )
            )
          )
        }))
        .subscribe({
          next: (resp) => {
            if (this.savedTrainingMaterialAttachments.length > 0) {
              courseToSave!.courseNumber = resp?.courseNumber;
              this.saveFiles(courseToSave!, isEditCourse);
            }
            else {
              this.closeAndResetCourseSlider$.subscribe();
              this.openCourseDetails(resp?.courseNumber)
            }
            this.messageService.add({
              severity: CourseConstants.Severity_Success,
              summary: `${CourseConstants.Course_Created_Success}`,
              life: 20000
            });
          }

        })
    }
  }

  constructTrainingMaterial() {
    let trainingMaterial = this.trainingMaterial$.getValue();
    let trainingMaterialSave: TrainingMaterialSave = {
      instructions: trainingMaterial?.instructions,
      urlLink: trainingMaterial?.urlLink,
      isURLVideo: trainingMaterial?.isURLVideo,
      relatedDocuments: trainingMaterial?.relatedDocuments?.map((relatedDocument) => { return relatedDocument.number }),
      attachments: trainingMaterial?.attachments
    };
    return trainingMaterialSave;
  }

  updateCourse(isEditCourse: boolean) {
    if (this.errorTabIndices.length === 0) {
      let courseToUpdate = this.courseDetails$.getValue();
      // if courseToUpdate detail from General Tab is not null, then Proceed for Save
      if (!!courseToUpdate) {
        const courseToUpdateDetails: Course = courseToUpdate;
        if (this.prerequisites$.getValue() !== null) {
          let prerequisites = this.prerequisites$.getValue()
          courseToUpdate!.prerequisites = prerequisites;
        }
        if (this.trainingMaterial$.getValue() !== null) {
          const trainingMaterialInfo = this.constructTrainingMaterial();
          courseToUpdate!.trainingMaterial = trainingMaterialInfo;
        }
        this.deleteTrainingMaterialFilesFromStorage().subscribe({
          next: () => {
            if (this.savedTrainingMaterialAttachments.length > 0) {
              this.saveFiles(courseToUpdateDetails, isEditCourse);
            }
            else {
              this.apiCallToUpdateCourse(courseToUpdateDetails);
              setTimeout(() => { window.location.reload() }, 2000);
            }
          },
          error: () => {
            if (this.savedTrainingMaterialAttachments.length > 0) {
              this.saveFiles(courseToUpdateDetails, isEditCourse);
            }
            else {
              this.apiCallToUpdateCourse(courseToUpdateDetails);
              setTimeout(() => { window.location.reload() }, 2000); // To Do- Update it with observable while doing it for other siilar implementations
            }
          }
        });
      }
    }
  }

  private deleteTrainingMaterialFilesFromStorage(): Observable<string[]> {
    if (!this.deletedCourseAttachments || this.deletedCourseAttachments.length === 0) {
      this.filesToDelete = [];
      return of([]);
    }

    return forkJoin(
      this.deletedCourseAttachments.map((attachment) =>
        this.courseService.deleteFile(attachment.attachmentFileId).pipe(
          map(() => attachment.attachmentFileId),
          catchError((error) => {
            console.error('Error deleting course attachment from storage:', attachment.attachmentFileId, error);
            return of('');
          })
        )
      )
    ).pipe(
      map((deletedFileIds) => {
        this.filesToDelete = deletedFileIds.filter((fileId) => fileId !== '');
        return this.filesToDelete;
      })
    );
  }

  openCourseDetails = (courseNumber: string | undefined) => {
    this.closeAndResetCourseSlider$.next(true);
    this.updateSaveInProgressStatus(false);
    setTimeout(() => {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([
          CourseConstants.Course_CourseDetailScreen + courseNumber,
        ])
      );
      window.open(url, '_blank');
    }, 1000);
  }

  updateTabErrorIndex = (tabIndex: number, isError: boolean) => {
    isError ? this.addErrorTabIndex(tabIndex) : this.removeErrorTabIndex(tabIndex);
    this.populateErrorsForTab$.next(true);
  }

  addErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) === -1) {
      this.errorTabIndices.push(tabIndex);
    }
  }

  removeErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) > -1) {
      this.errorTabIndices.splice(this.errorTabIndices.indexOf(tabIndex), 1);
    }
  }

  onCustomTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenuByCustomChange$.next(tabIndex);
  }

  onTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenu$.next(tabIndex);
  }


  updateDeletedAttachments = (courseAttachments: Attachment[]) => {
    this.deletedCourseAttachments = courseAttachments;
  }

  constructCourseAttachment = (fileName: string, fileSaveResponse: any): any => {
    return {
      attachmentFileId: fileSaveResponse.fileId,
      attachmentName: fileName,
      attachmentType: fileSaveResponse.fileExtension,
      attachmentPath: fileSaveResponse.filePath
    }
  }
  

saveFiles(course: Course, isEditCourse: boolean) {
  // If New File is Uploaded in Training Material
  const uploadObservables = this.savedTrainingMaterialAttachments.map(file => {
    const modifiedFile = new File(
      [file], 
      course.courseNumber + '/' + file.name, 
      { type: file.type }
    );
    
    return this.courseService.uploadFile(modifiedFile, 'eqmsdocumenttemplates')
      .pipe(
        map(response => ({ 
          file, 
          response,
          failed: false
        })),
        catchError((err) => {
          // Show specific error message for this file
          this.messageService.add({
            severity: CourseConstants.Severity_Err,
            summary: `Failed to upload file: ${file.name}`,
            detail: err || SharedConstants.File_Upload_Failed_Error,
            sticky:true
          });
          
          console.error('Upload failed for', file.name, err);
          return of({ 
            file, 
            response: null, 
            failed: true 
          });
        })
      );
  });

  forkJoin(uploadObservables).subscribe(results => {
    const trainingMaterial = this.trainingMaterial$.getValue();
    const failedUploads = results.filter(r => r.failed);
    const successfulUploads = results.filter(r => !r.failed);

    // Add successful uploads to training material
    successfulUploads.forEach(r => {
      if (r.response) {
        trainingMaterial?.attachments.push(
          this.constructCourseAttachment(r.file.name, r.response)
        );
      }
    });

    // Handle failed uploads
    if (failedUploads.length > 0) {
      const failedFileNames = failedUploads.map(f => f.file.name).join(', ');
      
      if (successfulUploads.length > 0) {
        // Partial success - some files uploaded, some failed
        this.messageService.add({
          severity: CourseConstants.Severity_Warning,
          summary: `${successfulUploads.length} file(s) uploaded successfully, ${failedUploads.length} failed`,
          detail: `Failed files: ${failedFileNames}`,
          sticky: true
        });
      } else {
        // All uploads failed
        const validationMessage = CourseConstants.File_Upload_Failed.replace(
          'Name',
          CourseConstants.CourseTrainingMaterials
        );
        this.messageService.add({
          severity: CourseConstants.Severity_Err,
          summary: validationMessage,
          detail: `Failed files: ${failedFileNames}`,
          sticky: true
        });
        return; // Don't proceed if all uploads failed
      }
    }

    // Update course with training material
    if (trainingMaterial) {
      course.trainingMaterial = this.constructTrainingMaterial();
    }

    // Empty the file list, so no duplication of files
    this.savedTrainingMaterialAttachments = [];
    
    if (!isEditCourse) {
      this.closeAndResetCourseSlider$.subscribe();
      this.openCourseDetails(course.courseNumber);
    }
    
    this.apiCallToUpdateCourse(course);
  });
}




  apiCallToUpdateCourse(course: Course) {
    this.courseService.updateCourse(course).subscribe({
      next: () => {
        this.messageService.add({
          severity: CourseConstants.Severity_Success,
          summary: `${CourseConstants.Course_Updated_Success}`,
        });
      }
    })
    setTimeout(() => { window.location.reload() }, 2000)
  }
}

